let _ = t => t,
  _t;
import { useEffect, useState, useRef, useCallback, useMemo, useContext } from 'react';
import I18n from 'I18n';
import InlineFeedback from 'ai-components-ui-library/components/feedback/InlineFeedback';
import { NOT_USING_TRACKER } from 'ai-components-ui-library/hooks/useTracker';
import { useSubmitFeedbackMutation } from '../../../__generated__/chirp/com/hubspot/transcript/be/rpc/FeedbackAppChirpService.hooks';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { setCallWithTranscriptFeedback } from '../../apis/UserSettingsApi';
import { addSuccessAlert } from '../../utils/alertMessageUtils';
import styledWithTheme from 'foundations-theming/utils';
import { TranscriptContext } from '../../contexts/TranscriptContext';
import { jsx as _jsx } from "react/jsx-runtime";
const StyledTranscriptFeedback = styledWithTheme.div(_t || (_t = _`
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${0};
  opacity: ${0};
  max-height: ${0};
  transition:
    opacity 0.3s 0s,
    max-height 0.5s 0.3s;

  & div[data-tflex='true'] {
    flex-grow: 0;
    inline-size: auto;
  }
`), ({
  theme
}) => theme.color['fill-surface-default'], ({
  showFeedback
}) => showFeedback ? 1 : 0, ({
  showFeedback
}) => showFeedback ? '80px' : '0');
const TranscriptFeedback = props => {
  const {
    callId,
    transcriptId,
    objectTypeId,
    onTranscriptFeedback
  } = props;
  const {
    usageTracker
  } = useContext(TranscriptContext);
  const [showFeedback, setShowFeedback] = useState(true);
  const feedbackTimeout = useRef(undefined);
  const [submitFeedback] = useSubmitFeedbackMutation();
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  useEffect(() => {
    return () => {
      if (feedbackTimeout.current) {
        clearTimeout(feedbackTimeout.current);
      }
    };
  }, []);
  const provideFeedback = useCallback(value => {
    if (!callId) return;
    if (onTranscriptFeedback) onTranscriptFeedback(value);
    setCallWithTranscriptFeedback(callId).catch(() => void 0);
    setFeedbackSubmitted(true);
    addSuccessAlert({
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "recordingReviewComponents.transcriptFeedback.confirmation"
      })
    });
    feedbackTimeout.current = setTimeout(() => setShowFeedback(false), 2000);
  }, [callId, onTranscriptFeedback]);
  const handlePositiveFeedback = useCallback(() => {
    if (!callId || feedbackSubmitted) return;
    if (!!transcriptId && objectTypeId) {
      void submitFeedback({
        variables: {
          transcriptId,
          transcribedCrmObjectId: Number(callId),
          transcribedCrmObjectTypeId: objectTypeId,
          vote: 1
        }
      });
    }
    provideFeedback(true);
  }, [callId, transcriptId, objectTypeId, feedbackSubmitted, submitFeedback, provideFeedback]);
  const handleNegativeFeedback = useCallback(({
    options = [],
    feedbackMessage
  }) => {
    if (!callId || feedbackSubmitted) return;
    if (transcriptId != null && objectTypeId) {
      void submitFeedback({
        variables: {
          transcriptId,
          transcribedCrmObjectId: Number(callId),
          transcribedCrmObjectTypeId: objectTypeId,
          vote: -1,
          feedbackGeneric: options,
          maybeFeedbackFreeform: feedbackMessage
        }
      });
    }
    provideFeedback(false);
  }, [callId, transcriptId, objectTypeId, feedbackSubmitted, submitFeedback, provideFeedback]);
  const negativeFeedbackPopoverTitle = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "recordingReviewComponents.transcriptFeedback.negativeFeedbackTitle"
  });
  const negativeFeedbackPopoverBodyOptions = useMemo(() => [{
    label: I18n.text('recordingReviewComponents.transcriptFeedback.negativeFeedbackOptions.inaccurateTranscription'),
    value: 'INACCURATE_TRANSCRIPTION'
  }, {
    label: I18n.text('recordingReviewComponents.transcriptFeedback.negativeFeedbackOptions.missingParts'),
    value: 'MISSING_PARTS'
  }, {
    label: I18n.text('recordingReviewComponents.transcriptFeedback.negativeFeedbackOptions.speakerAttributionIncorrect'),
    value: 'INCORRECT_SPEAKER_ATTRIBUTION'
  }, {
    label: I18n.text('recordingReviewComponents.transcriptFeedback.negativeFeedbackOptions.languageIssues'),
    value: 'LANGUAGE_ISSUES'
  }, {
    label: I18n.text('recordingReviewComponents.transcriptFeedback.negativeFeedbackOptions.other'),
    value: 'OTHER'
  }], []);
  if (feedbackSubmitted && !showFeedback) {
    return null;
  }
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(StyledTranscriptFeedback, {
      showFeedback: showFeedback,
      children: /*#__PURE__*/_jsx(InlineFeedback, {
        messageId: callId || '',
        usageTracker: usageTracker !== null && usageTracker !== void 0 ? usageTracker : NOT_USING_TRACKER,
        hideCopyButton: true,
        isFeedbackDisabled: feedbackSubmitted,
        useNegativeFeedbackPopover: true,
        onPositiveFeedback: handlePositiveFeedback,
        onNegativeFeedback: handleNegativeFeedback,
        negativeFeedbackPopoverTitle: negativeFeedbackPopoverTitle,
        negativeFeedbackPopoverBodyOptions: negativeFeedbackPopoverBodyOptions
      })
    })
  );
};
export default TranscriptFeedback;